import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import {
  AgentApprovalRequestRecord,
  AgencyRecord,
  AgencyUpsert,
  AgentRecord,
  ApiRepository,
  AuthCredentials,
  AuthUser,
  PosRecord,
  PosUpsertPayload,
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload,
  TaskRecord
} from './api-repository';

@Injectable({ providedIn: 'root' })
export class HttpRepository extends ApiRepository {
  private readonly http = inject(HttpClient);

  override login(payload: AuthCredentials): Promise<AuthUser | null> {
    return firstValueFrom(this.http.post<AuthUser>('/api/v1/auth/login', payload));
  }

  override requestSignup(payload: AuthCredentials & { fullName: string }): Promise<void> {
    return firstValueFrom(this.http.post<void>('/api/v1/auth/signup', payload));
  }

  override requestPasswordOtp(email: string): Promise<boolean> {
    return firstValueFrom(this.http.post<boolean>('/api/v1/auth/forgot-password/request-otp', { email }));
  }

  override verifyPasswordOtp(email: string, otp: string): Promise<boolean> {
    return firstValueFrom(this.http.post<boolean>('/api/v1/auth/forgot-password/verify-otp', { email, otp }));
  }

  override resetPassword(email: string, nextPassword: string): Promise<boolean> {
    return firstValueFrom(this.http.post<boolean>('/api/v1/auth/forgot-password/reset', { email, nextPassword }));
  }

  override getAgencies(): Promise<AgencyRecord[]> {
    return firstValueFrom(this.http.get<AgencyRecord[]>('/api/v1/agencies'));
  }

  override getAgencyById(id: string): Promise<AgencyRecord | null> {
    return firstValueFrom(this.http.get<AgencyRecord>(`/api/v1/agencies/${id}`));
  }

  override createAgency(payload: AgencyUpsert): Promise<AgencyRecord> {
    return firstValueFrom(this.http.post<AgencyRecord>('/api/v1/agencies', payload));
  }

  override updateAgency(id: string, payload: AgencyUpsert): Promise<AgencyRecord | null> {
    return firstValueFrom(this.http.put<AgencyRecord>(`/api/v1/agencies/${id}`, payload));
  }

  override getAgents(): Promise<AgentRecord[]> {
    return firstValueFrom(this.http.get<AgentRecord[]>('/api/v1/agents'));
  }

  override getAgentById(id: string): Promise<AgentRecord | null> {
    return firstValueFrom(this.http.get<AgentRecord>(`/api/v1/agents/${id}`));
  }

  override getPendingAgentRequests(): Promise<AgentApprovalRequestRecord[]> {
    return firstValueFrom(this.http.get<AgentApprovalRequestRecord[]>('/api/v1/agents/approval-requests'));
  }

  override approveAgentRequest(id: string): Promise<AgentRecord | null> {
    return firstValueFrom(this.http.post<AgentRecord>(`/api/v1/agents/${id}/approve`, {}));
  }

  override getPos(): Promise<PosRecord[]> {
    return firstValueFrom(this.http.get<PosRecord[]>('/api/v1/pos'));
  }

  override getPosById(id: string): Promise<PosRecord | null> {
    return firstValueFrom(this.http.get<PosRecord>(`/api/v1/pos/${id}`));
  }

  override createPos(payload: PosUpsertPayload): Promise<PosRecord> {
    return firstValueFrom(this.http.post<PosRecord>('/api/v1/pos', payload));
  }

  override updatePos(id: string, payload: PosUpsertPayload): Promise<PosRecord | null> {
    return firstValueFrom(this.http.put<PosRecord>(`/api/v1/pos/${id}`, payload));
  }

  override deletePos(id: string): Promise<boolean> {
    return firstValueFrom(this.http.delete<boolean>(`/api/v1/pos/${id}`));
  }

  override getSupervisors(): Promise<SupervisorRecord[]> {
    return firstValueFrom(this.http.get<SupervisorRecord[]>('/api/v1/supervisors'));
  }

  override getPendingSupervisorRequests(): Promise<SupervisorApprovalRequestRecord[]> {
    return firstValueFrom(this.http.get<SupervisorApprovalRequestRecord[]>('/api/v1/supervisors/approval-requests'));
  }

  override createSupervisor(payload: SupervisorUpsertPayload): Promise<SupervisorRecord> {
    return firstValueFrom(this.http.post<SupervisorRecord>('/api/v1/supervisors', payload));
  }

  override updateSupervisor(id: string, payload: SupervisorUpsertPayload): Promise<SupervisorRecord | null> {
    return firstValueFrom(this.http.put<SupervisorRecord>(`/api/v1/supervisors/${id}`, payload));
  }

  override approveSupervisorRequest(id: string): Promise<SupervisorRecord | null> {
    return firstValueFrom(this.http.post<SupervisorRecord>(`/api/v1/supervisors/${id}/approve`, {}));
  }

  override deleteSupervisor(id: string): Promise<boolean> {
    return firstValueFrom(this.http.delete<boolean>(`/api/v1/supervisors/${id}`));
  }

  override getTasks(): Promise<TaskRecord[]> {
    return firstValueFrom(this.http.get<TaskRecord[]>('/api/v1/tasks'));
  }
}
